import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { featurePath, FEATURE_ITEMS } from "@/lib/featureUsage";
import { isModuleEnabled } from "@/auth/modules";
import { setUsageUser, track, trackPageView, usageGeneration, flushUsage } from "@/usage";

/** 全站统一覆盖；只采集静态功能/操作标识，不读取文本、输入值或查询参数。 */
export default function FeatureUsageTracker({ enabled = true }: { enabled?: boolean }) {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const visit = useRef({ key: "", id: "", used: false });
  useEffect(() => {
    setUsageUser(me?.id ?? null);
    if (!me || !enabled) return;
    const feature = featurePath(pathname);
    if (!feature) return;
    const item = FEATURE_ITEMS.find(item => item.to === feature);
    if (item && !isModuleEnabled(me.preferences, item.module)) return;
    const generation = usageGeneration();
    const key = `${me.id}:${pathname}`;
    if (visit.current.key !== key) visit.current = { key, id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`, used: false };
    const state = visit.current;
    trackPageView(feature);
    let activeSince = document.visibilityState === "visible" ? performance.now() : null;
    let activeMs = 0;
    const markUsed = (source: string) => {
      if (state.used || generation !== usageGeneration() || document.visibilityState !== "visible") return;
      state.used = true;
      track("feature_use", feature, { visit_id: state.id, source });
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => { if (timer) clearTimeout(timer); if (document.visibilityState === "visible") timer = setTimeout(() => markUsed("visible_5s"), 5000); };
    schedule();
    const visibility = () => {
      if (activeSince != null) activeMs += performance.now() - activeSince;
      activeSince = document.visibilityState === "visible" ? performance.now() : null;
      schedule();
    };
    const interaction = (event: Event) => {
      if (generation !== usageGeneration() || !event.isTrusted || !(event.target instanceof Element)) return;
      const target = event.target.closest("button,a,input,select,form,[data-usage-action]");
      if (!target || !target.closest("main") || target.closest('[data-usage-ignore]')) return;
      if (target.matches("input,select") && event.type !== "change") return;
      if (target.matches("form") && event.type !== "submit") return;
      markUsed("interaction");
      const label = target.getAttribute("data-usage-action");
      const action = label && /^[a-z0-9_.-]{1,64}$/.test(label) ? label : `${event.type}.${target.tagName.toLowerCase()}`;
      track("action", `${feature}:${action}`, { visit_id: state.id });
    };
    const leave = () => {
      if (activeSince != null) activeMs += performance.now() - activeSince;
      activeSince = null;
      if (activeMs >= 1000 && generation === usageGeneration()) track("page_leave", feature, { duration_ms: Math.round(activeMs), visit_id: state.id });
      activeMs = 0;
    };
    const pagehide = () => { leave(); void flushUsage(); };
    window.addEventListener("pagehide", pagehide);
    document.addEventListener("visibilitychange", visibility);
    for (const name of ["click", "change", "submit"]) document.addEventListener(name, interaction, true);
    return () => {
      if (timer) clearTimeout(timer);
      leave();
      window.removeEventListener("pagehide", pagehide);
      document.removeEventListener("visibilitychange", visibility);
      for (const name of ["click", "change", "submit"]) document.removeEventListener(name, interaction, true);
    };
  }, [me?.id, me?.preferences, pathname, enabled]);
  return null;
}
